import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useI18n } from "@/lib/i18n";
import { Card, SectionTitle, EstimateView, useAct, ContactButtons } from "@/components/common";

function EstimateForm({ r, act, busy }) {
  const { t } = useI18n();
  const [e, setE] = useState({ labor: "", materials: "", currency: "NIO", notes: "" });
  const submit = () => act(`/requests/${r.id}/estimate`, { ...e, labor: Number(e.labor || 0), materials: Number(e.materials || 0) });
  return (
    <div className="grid grid-cols-2 gap-3" data-testid="estimate-form">
      <div><Label className="text-xs">{t("d.labor")}</Label><Input type="number" min="0" value={e.labor} onChange={(x) => setE({ ...e, labor: x.target.value })} data-testid="estimate-labor-input" /></div>
      <div><Label className="text-xs">{t("d.materials")}</Label><Input type="number" min="0" value={e.materials} onChange={(x) => setE({ ...e, materials: x.target.value })} data-testid="estimate-materials-input" /></div>
      <div className="col-span-2"><Label className="text-xs">{t("d.currency")}</Label>
        <select value={e.currency} onChange={(x) => setE({ ...e, currency: x.target.value })} className="w-full h-9 rounded-md border border-slate-300 px-2 text-sm" data-testid="estimate-currency-select">
          <option value="NIO">NIO (C$)</option><option value="USD">USD ($)</option>
        </select>
      </div>
      <div className="col-span-2"><Label className="text-xs">{t("d.notes")}</Label><Textarea rows={2} value={e.notes} onChange={(x) => setE({ ...e, notes: x.target.value })} data-testid="estimate-notes-input" /></div>
      <Button className="col-span-2 rounded-full bg-teal-600 hover:bg-teal-700" disabled={busy} onClick={submit} data-testid="estimate-submit-btn">{t("d.submit_est")}</Button>
    </div>
  );
}

function Assignment({ r, act, busy }) {
  const { t } = useI18n();
  const nav = useNavigate();
  const [reason, setReason] = useState("");
  const decline = async () => {
    try { await api.post(`/requests/${r.id}/respond`, { accept: false, reason }); toast.success(t("d.declined")); nav("/contractor"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card data-testid="assignment-panel">
      <SectionTitle>{t("d.respond")}</SectionTitle>
      <Button className="w-full rounded-full bg-teal-600 hover:bg-teal-700" disabled={busy} onClick={() => act(`/requests/${r.id}/respond`, { accept: true })} data-testid="accept-job-btn">{t("d.accept")}</Button>
      <Input className="mt-3" placeholder={t("d.reason")} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="decline-reason-input" />
      <Button variant="outline" className="w-full mt-2 rounded-full" disabled={busy} onClick={decline} data-testid="decline-job-btn">{t("d.decline")}</Button>
    </Card>
  );
}

function Progression({ r, act, busy }) {
  const { t } = useI18n();
  const [times, setTimes] = useState("");
  const [notes, setNotes] = useState("");
  return (
    <>
      <Card>
        <SectionTitle>{t("d.propose")}</SectionTitle>
        {r.appointments?.length > 0 && <ul className="text-sm mb-3" data-testid="contractor-appointments">{r.appointments.map((a, i) => <li key={i}>• {a.time}</li>)}</ul>}
        <Input placeholder={t("o.times_ph")} value={times} onChange={(e) => setTimes(e.target.value)} data-testid="propose-times-input" />
        <Button variant="outline" className="w-full mt-2 rounded-full" disabled={busy} onClick={() => act(`/requests/${r.id}/appointments`, { times: times.split(",") })} data-testid="propose-times-btn">{t("d.propose")}</Button>
      </Card>
      <Card>
        <SectionTitle>{t("d.set_status")}</SectionTitle>
        <div className="grid grid-cols-2 gap-2">
          {["scheduled", "in_progress"].map((s) => (
            <Button key={s} variant={r.status === s ? "default" : "outline"} className="rounded-full" disabled={busy} onClick={() => act(`/requests/${r.id}/status`, { status: s })} data-testid={`set-status-${s}`}>{t(`st.${s}`)}</Button>
          ))}
        </div>
        <Textarea className="mt-4" rows={3} placeholder={t("d.completion")} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="completion-notes-input" />
        <Button className="w-full mt-2 rounded-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold" disabled={busy} onClick={() => act(`/requests/${r.id}/status`, { status: "completed", completion_notes: notes })} data-testid="mark-complete-btn">{t("d.complete")}</Button>
      </Card>
    </>
  );
}

export default function ContractorPanel({ r, reload }) {
  const { t } = useI18n();
  const [busy, act] = useAct(reload);
  const [sent, setSent] = useState(false);
  const a = r.assignment?.status;
  if (a === "pending") return <Assignment r={r} act={act} busy={busy} />;
  const est = r.estimate;
  const done = ["completed", "closed"].includes(r.status);
  const confirm = async () => { await act(`/requests/${r.id}/messages`, { channel: "customer", body: t("d.confirm_client_msg", { number: r.number }) }, t("d.confirm_sent")); setSent(true); };
  return (
    <div className="space-y-6">
      <Card data-testid="contractor-client-card">
        <SectionTitle>{t("d.contact")}</SectionTitle>
        <p className="text-sm mb-3">{r.contact_name} · {r.contact_phone}</p>
        <ContactButtons phone={r.contact_phone} size="md" text={`${r.contact_name}, NicaFix ${r.number}`} />
        {!done && <Button variant="outline" className="w-full mt-4 rounded-full" disabled={busy || sent} onClick={confirm} data-testid="contractor-confirm-client-btn">{t("d.confirm_client")}</Button>}
      </Card>
      <Card data-testid="contractor-estimate">
        <SectionTitle>{t("d.estimate")}</SectionTitle>
        {est && <EstimateView est={est} />}
        {(!est || est.status === "rejected") && !done && <div className={est ? "mt-4" : ""}><EstimateForm r={r} act={act} busy={busy} /></div>}
        {est?.status === "pending_review" && <p className="text-xs text-amber-700 mt-2">{t("d.est_review")}</p>}
      </Card>
      {!done && <Progression r={r} act={act} busy={busy} />}
    </div>
  );
}
